import { Trophy } from "lucide-react";

const BattleSide = ({ label, name, total, isWinner, TXT, MUTED, CARD_BG, CARD_BORDER, WIN_COLOR, WIN_BG }) => (
  <div
    className="flex-1 rounded-2xl p-4 sm:p-6 flex flex-col items-center justify-center gap-2 transition-all"
    style={{
      background: isWinner ? WIN_BG : CARD_BG,
      border: `2px solid ${isWinner ? WIN_COLOR : CARD_BORDER}`,
      boxShadow: isWinner ? `0 0 24px ${WIN_COLOR}55` : 'none',
    }}
    data-testid={`results-side-${label}`}
  >
    {isWinner && <Trophy size={28} style={{ color: WIN_COLOR }} />}
    <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.95rem)', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
      {label}
    </div>
    <div
      style={{
        fontSize: 'clamp(1.1rem, 3.5vw, 1.75rem)',
        color: TXT,
        fontWeight: 800,
        textAlign: 'center',
        lineHeight: 1.1,
        maxWidth: '100%',
        overflowWrap: 'break-word',
      }}
      data-testid={`results-name-${label}`}
    >
      {name || '—'}
    </div>
    <div
      style={{
        fontSize: 'clamp(2.5rem, 10vw, 5rem)',
        color: isWinner ? WIN_COLOR : TXT,
        fontWeight: 900,
        lineHeight: 1,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontVariantNumeric: 'tabular-nums',
      }}
      data-testid={`results-total-${label}`}
    >
      {total}
    </div>
  </div>
);

/**
 * Fullscreen results overlay shown to all judges after every vote has
 * been revealed. Simple + responsive, disappears automatically when the
 * parent state transitions to a new battle/circle (the id-change effect
 * in JudgePage already clears revealed votes).
 */
export function BattleResults({ athlete1, athlete2, votes, isDark }) {
  const revealed = (votes || []).filter(v => v.revealed);
  const a1Total = revealed.reduce((s, v) => s + (v.athlete1_score || 0), 0);
  const a2Total = revealed.reduce((s, v) => s + (v.athlete2_score || 0), 0);
  let winner = null;
  if (a1Total > a2Total) winner = 'a1';
  else if (a2Total > a1Total) winner = 'a2';
  else {
    // Tie-break: count judge-level victories (matches backend logic).
    const a1w = revealed.filter(v => (v.athlete1_score || 0) > (v.athlete2_score || 0)).length;
    const a2w = revealed.filter(v => (v.athlete2_score || 0) > (v.athlete1_score || 0)).length;
    if (a1w > a2w) winner = 'a1';
    else if (a2w > a1w) winner = 'a2';
  }

  const TXT = isDark ? '#fff' : '#1a1a2e';
  const MUTED = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';
  const CARD_BG = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const CARD_BORDER = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const WIN_COLOR = '#10b981';
  const WIN_BG = isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)';

  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-4 py-6 gap-6" data-testid="battle-results">
      <div style={{ fontSize: 'clamp(0.85rem, 2vw, 1rem)', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
        RÉSULTATS
      </div>
      <div className="flex flex-row items-stretch gap-3 sm:gap-6 w-full max-w-3xl">
        <BattleSide label="a1" name={athlete1?.name} total={a1Total} isWinner={winner === 'a1'} TXT={TXT} MUTED={MUTED} CARD_BG={CARD_BG} CARD_BORDER={CARD_BORDER} WIN_COLOR={WIN_COLOR} WIN_BG={WIN_BG} />
        <BattleSide label="a2" name={athlete2?.name} total={a2Total} isWinner={winner === 'a2'} TXT={TXT} MUTED={MUTED} CARD_BG={CARD_BG} CARD_BORDER={CARD_BORDER} WIN_COLOR={WIN_COLOR} WIN_BG={WIN_BG} />
      </div>
    </div>
  );
}

export function CircleResults({ athletes, votes, forfeitedIds, isDark }) {
  const revealed = (votes || []).filter(v => v.revealed);
  const totals = {};
  (athletes || []).forEach(a => { if (a) totals[a.id] = 0; });
  revealed.forEach(v => {
    Object.entries(v.scores || {}).forEach(([aid, score]) => {
      if (totals[aid] !== undefined) totals[aid] += Number(score) || 0;
    });
  });
  const forfeit = new Set(forfeitedIds || []);
  const active = (athletes || []).filter(a => a && !forfeit.has(a.id));
  const ranked = active
    .slice()
    .sort((a, b) => {
      const diff = (totals[b.id] || 0) - (totals[a.id] || 0);
      if (diff !== 0) return diff;
      return (a.world_ranking || 9999) - (b.world_ranking || 9999);
    });

  const TXT = isDark ? '#fff' : '#1a1a2e';
  const MUTED = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';
  const CARD_BG = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const CARD_BORDER = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const WIN_COLOR = '#10b981';
  const WIN_BG = isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)';

  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-4 py-6 gap-4 overflow-y-auto" data-testid="circle-results">
      <div style={{ fontSize: 'clamp(0.85rem, 2vw, 1rem)', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
        RÉSULTATS
      </div>
      <div className="w-full max-w-md flex flex-col gap-2">
        {ranked.map((a, i) => {
          const isWinner = i === 0;
          return (
            <div
              key={a.id}
              className="rounded-xl px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-3 transition-all"
              style={{
                background: isWinner ? WIN_BG : CARD_BG,
                border: `2px solid ${isWinner ? WIN_COLOR : CARD_BORDER}`,
                boxShadow: isWinner ? `0 0 20px ${WIN_COLOR}44` : 'none',
              }}
              data-testid={`circle-results-rank-${i + 1}`}
            >
              <div
                style={{
                  minWidth: 36,
                  height: 36,
                  borderRadius: 10,
                  background: isWinner ? WIN_COLOR : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'),
                  color: isWinner ? '#fff' : TXT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 900,
                  fontSize: '1rem',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
                aria-hidden
              >
                {isWinner ? <Trophy size={18} /> : i + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ fontSize: 'clamp(0.95rem, 2.5vw, 1.15rem)', color: TXT, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  data-testid={`circle-results-name-${i + 1}`}
                >
                  {a.name}
                </div>
              </div>
              <div
                style={{
                  fontSize: 'clamp(1.3rem, 4vw, 2rem)',
                  color: isWinner ? WIN_COLOR : TXT,
                  fontWeight: 900,
                  fontVariantNumeric: 'tabular-nums',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
                data-testid={`circle-results-total-${i + 1}`}
              >
                {totals[a.id] || 0}
              </div>
            </div>
          );
        })}
        {[...(athletes || [])].filter(a => a && forfeit.has(a.id)).map((a, i) => (
          <div
            key={a.id}
            className="rounded-xl px-3 sm:px-4 py-2 flex items-center gap-3 opacity-50"
            style={{ background: CARD_BG, border: `1px dashed ${CARD_BORDER}` }}
          >
            <div
              style={{ minWidth: 36, height: 36, borderRadius: 10, background: 'transparent', color: MUTED, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}
            >—</div>
            <div style={{ flex: 1, fontSize: 'clamp(0.9rem, 2.2vw, 1.05rem)', color: MUTED, textDecoration: 'line-through' }}>
              {a.name}
            </div>
            <div style={{ fontSize: '0.75rem', color: MUTED, textTransform: 'uppercase', fontWeight: 700 }}>
              Forfait
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Helper: are ALL expected votes revealed?
 * For battles/circles we require: at least 1 vote AND every vote has revealed=true.
 */
export function allVotesRevealed(votes) {
  if (!Array.isArray(votes) || votes.length === 0) return false;
  return votes.every(v => v && v.revealed === true);
}
